import { formatNumber } from '@progress/kendo-intl';
import { DatePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import { TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { isWithinInterval } from 'date-fns';
import { findIndex, isEqual, maxBy, minBy } from 'lodash';
import moment from 'moment-timezone';
import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import uuid from 'react-uuid';
import AttachIcon from 'src/assets/icons/AttachIcon';
import UnAttachIcon from 'src/assets/icons/UnAttachIcon';
import {
  ButtonState,
  getButtonState,
  isButtonDisabled,
  isButtonHidden,
} from 'src/components/design-system/Button';
import {
  CanChangeMultipleInvoiceBudgetPeriods,
  CanChangeSingleInvoiceBudgetPeriod,
  CanUsesInitialBudgets,
} from 'src/constants/permissionsList';
import useAuth from 'src/hooks/useAuth';
import useBoolean from 'src/hooks/useBoolean';
import useCompany from 'src/hooks/useCompany';
import useConfirm from 'src/hooks/useConfirm';
import useDebounce from 'src/hooks/useDebounce';
import useDebounceCallback from 'src/hooks/useDebounceCallback';
import usePermission from 'src/hooks/usePermissions';
import usePostMessage from 'src/hooks/usePostMessage';
import useGetOcrResponseApi, {
  OcrFieldKeys,
  OcrInvoiceResponse,
  OcrItem,
  OcrLineItemsKeys,
} from 'src/services/useGetOcrResponseApi';
import useGlCodesApi, { GLCodeDropdownData, GlCodesObject } from 'src/services/useGlCodesApi';
import useGLCodesBudgetApi, {
  GLCodesBudgets,
  IGLCodesBudgetApiProps,
} from 'src/services/useGlCodesBudgetApi';
import useGLPeriodApi, { GlPeriodObject, GLPeriods } from 'src/services/useGLPeriodApi';
import useInvoiceDetailsApi, {
  InvoiceDetailsResponse,
  InvoiceTrigger,
} from 'src/services/useInvoiceDetailsApi';
import { InvoiceGlBreakoutItem } from 'src/services/useInvoiceGlBreakoutApi';
import useInvoiceGlBudgetSummaryApi, {
  generateGlCodeLabel,
  InvoiceGlBudgetSummaryResponse,
} from 'src/services/useInvoiceGlBudgetSummaryApi';
import { InvoiceLineItem, InvoiceLineItemWithWarnings } from 'src/services/useInvoiceLineItemsApi';
import { PoDetails } from 'src/services/usePoDetailsApi';
import useUpdateInvoiceApi from 'src/services/useUpdateInvoiceApi';
import { DropDownListData, SetState } from 'src/types/generic';
import { isFalse } from 'src/utils';
import { lastIndex } from 'src/utils/array';
import {
  getMatchingOcrLineItemsByReceiptLineItemId,
  MatchedOcrLineItem,
} from './utils/getMatchingOcrLineItems';

import { invoiceDirtyMessages, MIN_LINE_ITEMS } from 'src/constants/message';
import { fToMMDDYY, removeTime } from 'src/utils/formatTime';
import { hasPermission } from 'src/utils/JWT';
import { getHigherValueIndex, percentageCal, round } from 'src/utils/number';
import { isEqualString } from 'src/utils/strings';
import {
  formatGLPeriodDropDown,
  formatOcrResponse,
  GLPeriodDropdown,
} from 'src/utils/transformation';
import { createContext, useContextSelector } from 'use-context-selector';
import { PoRelatedDocumentsContext } from '../po_related_documents/PoRelatedDocumentsContext';
import { DistributionPermissions } from '../view_invoice_budget_period';
import useViewInvoiceBudgetTableErrors, {
  BudgetLineItemError,
  BudgetLineItemInfo,
} from '../view_invoice_budget_period/useViewInvoiceBudgetTableErrors';
import useInvoiceDetailsErrors, {
  InvoiceDetailsErrors,
  InvoiceDetailsInformations,
  InvoiceDetailsWarnings,
} from './errors/useInvoiceDetailsErrors';
import {
  defaultInvoiceLineItemError,
  defaultInvoiceLineItemInfos,
  defaultInvoiceLineItemWarning,
  InvoiceLineItemInfo,
} from './errors/useInvoiceLineItemErrors';
import { calculateTotals } from './useGlDistributionsGrandTotals';

export interface InvoiceBudgetPeriodResponse {
  glPeriodId: number;
  amount: number;
  percentage: number;
  percentageAmount: number;
}

export interface ErrorMessage {
  totalAmount: string[];
  totalPercentage: string[];
}
export const getEditPeriodsButtonText = (
  isEditPeriodsTxt: boolean,
  isEditPeriodTxt: boolean,
  isConvertPeriodsTxt: boolean,
  isConvertPeriodTxt: boolean
): string =>
  isEditPeriodsTxt
    ? 'Edit Periods'
    : isEditPeriodTxt
    ? 'Edit Period'
    : isConvertPeriodsTxt
    ? 'Convert Periods'
    : isConvertPeriodTxt
    ? 'Convert Period'
    : '';

const getLastYearPeriod = (array: GLPeriods) => {
  const lastYear = moment().subtract(1, 'year').year();
  const index = lastIndex(array, (value) => value.year === lastYear);
  if (index === -1) {
    return array.slice(0, array.length);
  }
  return array.slice(0, index + 1);
};

function getAllFuturePeriods(array: GLPeriods, index: number) {
  const futurePeriods = array.slice(0, index + 1);
  return futurePeriods;
}

function getPermissionNumber(
  isInvoiceEditable: boolean,
  isPOMPA: boolean,
  hasCanChangeSinglePeriodPermission: boolean,
  hasCanChangeMultiplePeriodsPermission: boolean,
  isInvoiceMPA: boolean
) {
  const binaryCombination =
    (isInvoiceEditable ? '1' : '0') +
    (isPOMPA ? '1' : '0') +
    (hasCanChangeSinglePeriodPermission ? '1' : '0') +
    (hasCanChangeMultiplePeriodsPermission ? '1' : '0') +
    (isInvoiceMPA ? '1' : '0');

  const index = 32 - parseInt(binaryCombination, 2);
  return index;
}

const generateInvoiceGlCodeLabel = (
  label: string,
  forecast: number,
  initialBudget: number,
  hasUsesInitialBudgetsPermission: boolean
) => {
  const forecastBalance = formatNumber(Number(forecast ?? 0), 'c2');
  if (hasUsesInitialBudgetsPermission) {
    const budgetBalance = initialBudget != null ? formatNumber(Number(initialBudget), 'c2') : 'N/A';
    return `${label}.\nBudget Balance: ${budgetBalance}\nForecast Balance: ${forecastBalance}`;
  }
  return `${label}\nForecast Balance: ${forecastBalance}`;
};

export interface IInvoiceBudgetPeriod {
  glPeriodId: number;
  amount: number;
  percentageAmount: number;
  isDeletable: boolean;
  rowId: string;
  expanded: boolean;
  isClosed?: number;
}

interface InvoiceContextType {
  type: 'invoice' | 'credit';
  poId: string;
  title: string;
  invoice: InvoiceDetailsResponse;
  initialInvoice: InvoiceDetailsResponse;
  expandedGLCode: string[];
  setExpandedGLCode: SetState<string[]>;
  setInvoice: SetState<InvoiceDetailsResponse>;
  getInvoice: () => Promise<InvoiceDetailsResponse | undefined>;
  glCodeOptions: GLCodeDropdownData;
  glCodesObject: GlCodesObject;
  invoiceLineItems: InvoiceLineItem[];
  initialInvoiceLineItems: InvoiceLineItem[];
  setInvoiceLineItems: SetState<InvoiceLineItem[]>;
  invoiceGlCodes: InvoiceGlBreakoutItem[];
  setInvoiceGlCodes: SetState<InvoiceGlBreakoutItem[]>;
  handleInputChange: (
    key: string
  ) => ({ value }: TextBoxChangeEvent | DatePickerChangeEvent) => void;
  showScans: boolean;
  setShowScans: SetState<boolean>;
  invoiceId: string;
  companyId: string;
  splitOption: string;
  setSplitOption: SetState<string>;
  splitLineItem: InvoiceLineItem[];
  setSplitLineItem: SetState<InvoiceLineItem[]>;
  errors: InvoiceDetailsErrors;
  warnings: InvoiceDetailsWarnings;
  informations: InvoiceDetailsInformations;
  totalAmount: (field: string) => number;
  setApIdDirty: VoidFunction;
  setSupplierNameDirty: VoidFunction;
  hasErrors: boolean;
  isInvoiceAPIDDirty: boolean;
  hasWarnings: boolean;
  isInvoiceInProgress: boolean;
  isInvoiceEditDisabled: boolean;
  isInvoiceLoading: boolean;
  hasDuplicateInvoiceNumber: boolean;
  refreshPage: () => Promise<void>;
  setLinkedReceiptQuantity: SetState<number | null>;
  linkedReceiptQuantity: number | null;
  isInvoiceSupplierNameDirty: boolean;
  errorList: string[];
  warningList: string[];
  isInvoiceDirty: boolean;
  isCreditMemo: boolean;
  lineItemInfos: InvoiceLineItemInfo[];
  setIsGlCodesDirty: SetState<boolean>;
  lineItemsWithErrorWarningStatus: InvoiceLineItem[];
  isInvoiceComplete: boolean;
  isValidatingDuplicateInvoiceNumber: boolean;
  selectedLineItemIds: number[];
  setSelectedLineItemIds: SetState<number[]>;
  expandedLineItemIds: number[];
  setExpandedLineItemIds: SetState<number[]>;
  receiptLineItemIds: number[];
  saveInvoice: (glPeriodIdStartDate: string) => Promise<any>;
  isUpdatingInvoice: boolean;
  getMessages: (buttonState: ButtonState, messages: string[]) => string[];
  isInvoiceActionButtonDisabled: (buttonState: ButtonState) => boolean;
  isLineItemClick: boolean;
  setIsLineItemClick: SetState<boolean>;
  glBreakoutHasBothNegativeAndPositiveValues: boolean;
  isOpenMpa: boolean;
  openMpa: () => void;
  closeMpa: () => void;
  poDetails: PoDetails;
  isPOMPA: boolean;

  invoiceBudgetPeriods: IInvoiceBudgetPeriod[];
  setInvoiceBudgetPeriods: SetState<IInvoiceBudgetPeriod[]>;
  isEditPeriodDisable: boolean;
  editPeriodErrorMessage: string[];
  getLinkIcon: () => JSX.Element | null;
  isInherited: boolean;
  ApIdandExternalSupplierNametooltipMsg: string;
  invoiceGlBudgetSummary: InvoiceGlBudgetSummaryResponse[];
  getInvoiceGlBudgetSummary: () => Promise<InvoiceGlBudgetSummaryResponse[]>;
  hasUsesInitialBudgetsPermission: boolean;
  formattedGlCodeOptions: DropDownListData;
  isDropDownLoading: boolean;
  isInvoiceBudgetPeriodDirty: boolean;
  isInitiallyLoading: boolean;

  dropDownValues: GLPeriodDropdown[];
  distributionType: 'Percentage' | 'Amount';
  setDistributionType: SetState<'Percentage' | 'Amount'>;
  getGlCodesBudget: (params?: IGLCodesBudgetApiProps | undefined) => Promise<GLCodesBudgets>;
  budgetErrors: BudgetLineItemError[];
  infos: BudgetLineItemInfo[];
  invoiceBudgetPeriodDataWithErrors: Record<string, any>[];
  totalPercentage: number;
  budgetPeriodsTotalAmount: number;
  budgetPeriodsHasErrors: boolean;
  footerInfoMessage: string[];
  errorMessage: ErrorMessage;
  informationList: string[];
  saveErrorList: string[];
  errorMessageList: string[];
  glPeriods: GLPeriods;
  singleGlPeriodStartDate: string;
  setSingleGlPeriodStartDate: SetState<string>;
  confirmBeforeProceeding: (acceptCallback: VoidFunction) => void;
  hasDistributionPermission: boolean;
  mpaPermissionNumber: number;
  isInvoiceMPA: boolean;
  handleOpenSidePanel: VoidFunction;
  handleCancelSidePanel: VoidFunction;
  isInvoiceMPAEditable: boolean;
  expandedBudgetPeriodIds: string[];
  setExpandedBudgetPeriodIds: SetState<string[]>;

  throttledInvoiceGLCodes: InvoiceGlBreakoutItem[];
  throttledInvoiceLineItems: InvoiceLineItem[];
  throttledInvoiceBudgetPeriods: IInvoiceBudgetPeriod[];
  areBudgetsThrottling: boolean;
  areLineItemsThrottling: boolean;
  areGLCodesThrottling: boolean;
  areLineItemsDebouncing: boolean;
  setAreLineItemsDebouncing: SetState<boolean>;
  calculateGLDistributionData: (
    InvoiceGlBreakoutItem: InvoiceGlBreakoutItem[]
  ) => InvoiceGlBreakoutItem[];
  hasGLChanged: boolean;
  handleHoverOcrField: (field: string, index?: number[]) => void;
  getMatchingOcrLineItem: (recvReceiptLineItemId: number) => MatchedOcrLineItem[];
  ocrResponse: OcrInvoiceResponse;
  numLineItems: number;
  setNumLineItems: SetState<number>;
}

export const updateAllocationDecimal = (
  periodAllocation: InvoiceBudgetPeriodResponse[],
  invoiceTotal: number
) => {
  let actualTotal = 0;
  let roundedTotal = 0;
  periodAllocation.forEach((item) => {
    actualTotal += item.amount;
    roundedTotal = round(roundedTotal + item.amount, 2);
  });
  const twoDecimalActualTotal = round(actualTotal, 2);

  if (twoDecimalActualTotal === invoiceTotal && twoDecimalActualTotal !== roundedTotal) {
    const remainingAmount = round(twoDecimalActualTotal - roundedTotal, 2);
    let maxAmtIndex = findIndex(periodAllocation, maxBy(periodAllocation, 'amount'));
    let minAmtIndex = findIndex(periodAllocation, minBy(periodAllocation, 'amount'));

    if (remainingAmount < 0) {
      periodAllocation[maxAmtIndex].amount = round(
        periodAllocation[maxAmtIndex].amount - remainingAmount * -1,
        2
      );
    } else if (remainingAmount > 0) {
      periodAllocation[minAmtIndex].amount = round(
        periodAllocation[minAmtIndex].amount + remainingAmount,
        2
      );
    }
    return periodAllocation.map((item) => ({ ...item, amount: round(item.amount, 2) }));
  }
  return periodAllocation;
};

export const generateInitialAllocations = (invoice: InvoiceDetailsResponse) => {
  const isPeriodAllocationsEmpty = !invoice?.periodAllocations?.length;
  const grandTotalAmt = Number(invoice.totalCostAmt) || 0;

  let initialInvoiceBudgetPeriods = [];
  if (isPeriodAllocationsEmpty && invoice) {
    const currentglPeriodId = invoice?.glPeriodId;
    initialInvoiceBudgetPeriods = [
      {
        glPeriodId: currentglPeriodId,
        percentageAmount: 100,
        amount: grandTotalAmt,
        isDeletable: false,
        rowId: uuid(),
        expanded: false,
      },
    ];
  } else {
    initialInvoiceBudgetPeriods =
      invoice?.periodAllocations?.map((item, indx) => {
        const percentageAmount = item.percentage;
        return {
          ...item,
          percentageAmount: percentageAmount,
          isDeletable: indx !== 0,
          rowId: uuid(),
          expanded: false,
        };
      }) || [];
  }
  return initialInvoiceBudgetPeriods;
};

export const InvoiceContext = createContext<InvoiceContextType>({} as InvoiceContextType);

interface InvoiceContextProps {
  children: ReactNode;
  setPoId: SetState<string>;
}

const InvoiceDetailsContextProvider: FC<InvoiceContextProps> = ({ children, setPoId }) => {
  // states
  const [selectedLineItemIds, setSelectedLineItemIds] = useState<number[]>([]);
  const [expandedLineItemIds, setExpandedLineItemIds] = useState<number[]>([]);
  const [expandedGLCode, setExpandedGLCode] = useState<string[]>([]);
  const [isGlCodesDirty, setIsGlCodesDirty] = useState(false);
  const [showScans, setShowScans] = useState(true);
  const [isInitiallyLoading, setIsInitiallyLoading] = useState(true);
  const [splitOption, setSplitOption] = useState<string>('Percentage');
  const [splitLineItem, setSplitLineItem] = useState<InvoiceLineItem[]>([]);
  const [linkedReceiptQuantity, setLinkedReceiptQuantity] = useState<number | null>(null);
  const [distributionType, setDistributionType] = useState<'Percentage' | 'Amount'>('Percentage');
  const [singleGlPeriodStartDate, setSingleGlPeriodStartDate] = useState<string>('');
  const [expandedBudgetPeriodIds, setExpandedBudgetPeriodIds] = useState<string[]>([]);
  const { value: isOpenMpa, setTrue: openMpa, setFalse: closeMpa } = useBoolean(false);
  const { value: isLineItemClick, setBool: setIsLineItemClick } = useBoolean(false);
  const [areLineItemsDebouncing, setAreLineItemsDebouncing] = useState(false);
  const initialCalculate = useRef(false);
  const [numLineItems, setNumLineItems] = useState<number>(MIN_LINE_ITEMS);

  //contexts
  const refreshPoDetails = useContextSelector(
    PoRelatedDocumentsContext,
    (value) => value.refreshPoDetails
  );
  const poDetails = useContextSelector(PoRelatedDocumentsContext, (value) => value.poDetails);
  const isPoLoading = useContextSelector(PoRelatedDocumentsContext, (value) => value.isLoading);

  // other hooks
  const [searchParams] = useSearchParams();
  const { confirm } = useConfirm();
  const { user, companies } = useAuth();
  const { changeCompany } = useCompany();

  // state derived variables
  const invoiceId = useMemo(() => searchParams.get('recid') || '', [searchParams]);

  // services
  const {
    invoice,
    setInvoice,
    getInvoice,
    isInvoiceLoading,
    isInvoiceDirty: isInvoiceDetailsDirty,
    isInvoiceAPIDDirty,
    isInvoiceSupplierNameDirty,
    initialInvoice,
    invoiceGlCodes,
    setInvoiceGlCodes,
    initialInvoiceLineItems,
    setInitialInvoiceLineItems,
    invoiceLineItems,
    setInvoiceLineItems,
    isInvoiceLineItemsDirty,
    invoiceBudgetPeriods,
    setInvoiceBudgetPeriods,
  } = useInvoiceDetailsApi(invoiceId);

  const poId = useMemo(
    () => searchParams.get('poid') || invoice?.poId?.toString(),
    [invoice?.poId, searchParams]
  );

  useEffect(() => {
    setPoId(poId);
  }, [setPoId, poId]);

  const throttledInvoiceGLCodes: InvoiceGlBreakoutItem[] = invoiceGlCodes;
  const throttledInvoiceBudgetPeriods = useDebounce(invoiceBudgetPeriods, 1000);
  const throttledInvoiceLineItems = useDebounce(invoiceLineItems, 1000);

  const areGLCodesThrottling = false;
  const areBudgetsThrottling = false;
  const areLineItemsThrottling = false;
  const companyId = useMemo(() => invoice?.companyId?.toString(), [invoice?.companyId]);

  const { glCodeOptions, glCodesObject, areGLCodesLoading } = useGlCodesApi(
    companyId,
    invoice?.glPeriodId,
    true
  );
  const { updateInvoice, isUpdatingInvoice } = useUpdateInvoiceApi(invoiceId);

  const {
    invoiceGlBudgetSummary,
    getInvoiceGlBudgetSummary,
    isLoading: isInvoiceGLBudgetSummaryLoading,
  } = useInvoiceGlBudgetSummaryApi(invoiceId);

  const {
    getGLPeriods,
    glPeriods,
    isLoading: isDropDownLoading,
    glPeriodsObject,
  } = useGLPeriodApi(companyId);

  // permissions
  const hasUsesInitialBudgetsPermission: boolean = usePermission({
    permissions: CanUsesInitialBudgets,
    companyAttribute: true,
  });
  const isSingleBudgetPeriod = usePermission({
    permissions: CanChangeSingleInvoiceBudgetPeriod,
    or: true,
  });
  const isMultiBudgetPeriods = usePermission({
    permissions: CanChangeMultipleInvoiceBudgetPeriods,
    or: true,
  });

  // side effects
  useEffect(() => {
    if (companyId) getGLPeriods();
  }, [getGLPeriods, companyId]);
  const { getOcrResponse, ocrResponse, setOcrResponse } = useGetOcrResponseApi();

  useEffect(() => {
    if (companyId) {
      const company = companies.find((company) => company.companyId === companyId);
      if (company) {
        changeCompany(company);
      }
    }
  }, [changeCompany, companies, companyId]);

  const isInvoiceMPA = useMemo(
    () => invoiceBudgetPeriods?.length > 1,
    [invoiceBudgetPeriods?.length]
  );

  useEffect(() => {
    const lineItems = (invoice?.lineItems?.map((lineItem) => ({
      ...lineItem,
      rowId: uuid(),
      completed: !(lineItem.poQty > lineItem.previousQty + lineItem.quantityReceived) ? 'Y' : 'N',
      isTaxable: !!lineItem?.isTaxable,
      expanded: false,
      selected: false,
      hasErrorsOrWarnings: false,
    })) || []) as InvoiceLineItem[];
    setInitialInvoiceLineItems(lineItems);
    setInvoiceLineItems(lineItems);
  }, [invoice?.lineItems, setInitialInvoiceLineItems, setInvoiceLineItems]);

  useEffect(() => {
    if (
      isFalse(isPoLoading) &&
      isFalse(isInvoiceLoading) &&
      isFalse(areGLCodesLoading) &&
      isFalse(isInvoiceGLBudgetSummaryLoading) &&
      isFalse(isDropDownLoading)
    ) {
      setIsInitiallyLoading(false);
    }
  }, [
    areGLCodesLoading,
    isDropDownLoading,
    isInvoiceGLBudgetSummaryLoading,
    isInvoiceLoading,
    isPoLoading,
  ]);

  const handleOcrFieldHighlight = useCallback(
    (field: string, index?: number[]) => {
      if (invoice.ocrGenerated) {
        setOcrResponse((prev: any) => {
          const data = { ...prev };
          const newData: OcrInvoiceResponse = formatOcrResponse(data);
          if (index && index?.length > 0) {
            for (let i = 0; i < index.length; i++) {
              const element = index[i];
              const lineItem: OcrItem = newData.items[element]?.item;
              if (!lineItem) {
                continue;
              }
              if (typeof lineItem[field as OcrLineItemsKeys] === 'object') {
                lineItem[field as OcrLineItemsKeys] = {
                  ...lineItem[field as OcrLineItemsKeys],
                  isHighlight: true,
                };
              }
            }
          } else if (typeof newData[field as OcrFieldKeys] === 'object') {
            newData[field as OcrFieldKeys] = {
              ...newData[field as OcrFieldKeys],
              isHighlight: true,
            };
          }
          return newData;
        });
      }
    },
    [invoice.ocrGenerated, setOcrResponse]
  );

  const getMatchingOcrLineItem = useCallback(
    (recvReceiptLineItemId: number) =>
      getMatchingOcrLineItemsByReceiptLineItemId(ocrResponse.items, recvReceiptLineItemId),
    [ocrResponse.items]
  );

  const [handleHoverOcrField] = useDebounceCallback(handleOcrFieldHighlight, 1000);

  const type = useMemo(
    () =>
      invoice
        ? (
            typeof invoice.totalCostAmt === 'number'
              ? invoice.totalCostAmt < 0
              : parseFloat(invoice.totalCostAmt) < 0
          )
          ? 'credit'
          : 'invoice'
        : 'invoice',
    [invoice]
  );
  const title = useMemo(() => (type === 'credit' ? 'Credit Memo' : 'Invoice'), [type]);
  const isCreditMemo = useMemo(() => type === 'credit', [type]);
  const isInvoiceInProgress = useMemo(
    () => isEqualString(invoice?.status, 'in progress'),
    [invoice?.status]
  );
  const isInvoiceComplete = useMemo(
    () => isEqualString(invoice?.status, 'complete'),
    [invoice?.status]
  );
  const isInvoiceEditDisabled = useMemo(() => {
    const [, updateButtonState] = getButtonState(
      invoice.triggerPreCheck,
      'Update',
      InvoiceTrigger as unknown as string[]
    );
    const isUpdateButtonDisabled = isButtonDisabled(updateButtonState);
    const isUpdateButtonHidden = isButtonHidden(updateButtonState);
    return !isInvoiceInProgress || isUpdateButtonHidden || isUpdateButtonDisabled;
  }, [invoice.triggerPreCheck, isInvoiceInProgress]);

  const isInvoiceBudgetPeriodDirty = useMemo(() => {
    if (invoiceBudgetPeriods.length === 1 && invoice?.periodAllocations?.length === 0) {
      return (
        invoice?.glPeriodId !== invoiceBudgetPeriods[0]?.glPeriodId ||
        invoiceBudgetPeriods[0]?.percentageAmount !== 100
      );
    }
    const multiPeriodData = invoiceBudgetPeriods.map((item) => ({
      glPeriodId: item.glPeriodId,
      amount: Number(item.amount),
    }));
    const multiBudgetPeriodsRows = invoice?.periodAllocations?.map((item) => ({
      glPeriodId: item.glPeriodId,
      amount: Number(item.amount),
    }));

    return !isEqual(multiPeriodData, multiBudgetPeriodsRows);
  }, [invoice?.glPeriodId, invoice?.periodAllocations, invoiceBudgetPeriods]);

  const hasGLChanged = useMemo(
    () =>
      isGlCodesDirty ||
      isInvoiceLineItemsDirty ||
      Number(initialInvoice?.totalCostAmt) !== Number(invoice?.totalCostAmt) ||
      Number(initialInvoice?.discountAmt) !== Number(invoice?.discountAmt) ||
      Number(initialInvoice?.shipCostAmt) !== Number(invoice?.shipCostAmt) ||
      Number(initialInvoice?.salesTaxAmt) !== Number(invoice?.salesTaxAmt),
    [
      isGlCodesDirty,
      isInvoiceLineItemsDirty,
      initialInvoice?.totalCostAmt,
      initialInvoice?.discountAmt,
      initialInvoice?.shipCostAmt,
      initialInvoice?.salesTaxAmt,
      invoice?.totalCostAmt,
      invoice?.discountAmt,
      invoice?.shipCostAmt,
      invoice?.salesTaxAmt,
    ]
  );

  const {
    errors,
    warnings,
    setApIdDirty,
    setSupplierNameDirty,
    hasErrors,
    hasWarnings,
    errorList,
    warningList,
    hasDuplicateInvoiceNumber,
    lineItemInfos,
    informations,
    isValidatingDuplicateInvoiceNumber,
  } = useInvoiceDetailsErrors(
    invoice,
    invoiceGlCodes?.[invoiceGlCodes?.length - 1],
    invoiceLineItems,
    throttledInvoiceGLCodes,
    isInvoiceLoading,
    isInvoiceInProgress,
    isInvoiceEditDisabled,
    glCodeOptions,
    isCreditMemo,
    initialInvoice.invoiceNumber,
    invoice?.periodAllocations || [],
    glPeriods,
    glCodesObject
  );

  const totalAmount = useCallback(
    (field: string) =>
      [...splitLineItem].reduce(
        (acc, current: { [x: string]: any }) => acc + Number(current[field] || 0),
        0
      ),
    [splitLineItem]
  );

  const handleInputChange = useCallback(
    (key: string) =>
      ({ value }: TextBoxChangeEvent | DatePickerChangeEvent) => {
        setInvoice((prev) => ({
          ...prev,
          [key]: value,
        }));
      },
    [setInvoice]
  );

  const refreshPage = useCallback(async () => {
    await refreshPoDetails();
    await getInvoice();
    await getInvoiceGlBudgetSummary();
  }, [getInvoice, getInvoiceGlBudgetSummary, refreshPoDetails]);
  useEffect(() => {
    if (invoice?.ocrGenerated && invoice.attachments.length > 0) {
      getOcrResponse(invoice?.attachments[0]?.attachId);
    } else {
      setOcrResponse({} as OcrInvoiceResponse);
    }
  }, [getOcrResponse, invoice.attachments, invoice?.ocrGenerated, setOcrResponse]);

  // need separate thread
  const lineItemsWithErrorWarningStatus: InvoiceLineItemWithWarnings[] = useMemo(
    () =>
      invoiceLineItems.map((item, index) => ({
        ...item,
        previousQtys:
          (
            ((Number(item.quantityReceived) + Number(item.previousQty)) / Number(item.poQty)) *
            100
          )?.toFixed(2) || 0.0,
        errors: errors?.lineItems?.[index] || defaultInvoiceLineItemError,
        warnings: warnings?.lineItems?.[index] || defaultInvoiceLineItemWarning,
        informations: informations?.lineItems[index] || defaultInvoiceLineItemInfos,
        hasErrorsOrWarnings:
          Object.values(errors?.lineItems?.[index] || {})?.flat()?.length > 0 ||
          Object.values(warnings?.lineItems?.[index] || {})?.flat()?.length > 0 ||
          Object.values(informations?.lineItems?.[index] || {})?.flat()?.length > 0 ||
          false,
        selected: selectedLineItemIds.includes(item.receiptLineItemId),
        expanded: expandedLineItemIds.includes(item.receiptLineItemId),
        glCodeDescription: glCodesObject?.[item?.glCd]?.label,
        poGlCodeDescription: glCodesObject?.[item?.poGlCd]?.label,
      })),
    [
      errors?.lineItems,
      expandedLineItemIds,
      glCodesObject,
      informations?.lineItems,
      invoiceLineItems,
      selectedLineItemIds,
      warnings?.lineItems,
    ]
  );

  const receiptLineItemIds = useMemo(
    () => throttledInvoiceLineItems.map((item) => item.receiptLineItemId),
    [throttledInvoiceLineItems]
  );

  const isInvoiceDirty = useMemo(
    () => isInvoiceDetailsDirty || isInvoiceLineItemsDirty || isGlCodesDirty,
    [isGlCodesDirty, isInvoiceDetailsDirty, isInvoiceLineItemsDirty]
  );

  const getMessages = useCallback(
    (buttonState: ButtonState, messages: string[]) => [
      ...(isInvoiceDirty && !hasErrors
        ? invoiceDirtyMessages
        : isInvoiceDirty && hasErrors
        ? [...errorList]
        : [
            ...errorList,
            ...(isInvoiceDirty ? invoiceDirtyMessages : []),
            ...warningList,
            ...(isButtonDisabled(buttonState) ? messages : []),
          ]),
    ],
    [errorList, hasErrors, isInvoiceDirty, warningList]
  );

  const isInvoiceActionButtonDisabled = useCallback(
    (buttonState: ButtonState) =>
      isButtonDisabled(buttonState) || hasWarnings || hasErrors || isInvoiceDirty,

    [hasErrors, hasWarnings, isInvoiceDirty]
  );

  const glBreakoutHasBothNegativeAndPositiveValues = useMemo(() => {
    const hasSomeLineItemNegative = throttledInvoiceGLCodes.some(
      (lineItem) => lineItem.glSubTotal < 0
    );
    const hasSomeLineItemPositive = throttledInvoiceGLCodes.some(
      (lineItem) => lineItem.glSubTotal > 0
    );

    return hasSomeLineItemNegative && hasSomeLineItemPositive;
  }, [throttledInvoiceGLCodes]);

  useEffect(() => {
    if (glBreakoutHasBothNegativeAndPositiveValues && invoice.isAutoDistGlBreakout) {
      confirm({
        title: 'Auto-Distribute Disabled',
        content: (
          <div>
            <p>
              Use of a negative GL subtotal on an invoice requires manual distribution of Discount,
              Shipping, and Tax values.
            </p>
            <br />
            <p>
              Please adjust the GL code(s) with the negative values to restore access to the
              Auto-Distribute feature.
            </p>
          </div>
        ),
        hasNoConfirmButton: true,
        cancelButtonText: 'Close',
      }).then(() => {
        setInvoiceGlCodes((prev) => [
          ...prev.map((i: InvoiceGlBreakoutItem) => ({
            ...i,
            salesTaxAmt: 0,
            shipCostAmt: 0,
            discountAmt: 0,
          })),
        ]);
      });
      setInvoice((prev) => ({ ...prev, isAutoDistGlBreakout: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [glBreakoutHasBothNegativeAndPositiveValues, invoice?.isAutoDistGlBreakout]);

  const dirtyMessage = useMemo(() => ({ isDirty: isInvoiceDirty }), [isInvoiceDirty]);
  usePostMessage(dirtyMessage);
  const isPOMPA = useMemo(() => !!invoice?.isPoMpa, [invoice?.isPoMpa]);

  const getLinkIcon = useCallback(() => {
    if (invoice?.supplierInheritance === 'Inherited') {
      return <AttachIcon />;
    }
    if (invoice?.supplierInheritance === 'NotInherited') {
      return <UnAttachIcon />;
    }
    return null;
  }, [invoice?.supplierInheritance]);

  const isInherited = useMemo(
    () => invoice?.supplierInheritance === 'Inherited',
    [invoice?.supplierInheritance]
  );
  const isNotInherited = useMemo(
    () => invoice?.supplierInheritance === 'NotInherited',
    [invoice?.supplierInheritance]
  );

  const ApIdandExternalSupplierNametooltipMsg = useMemo(() => {
    if (isInherited) {
      return 'Inherited from Management Company. Changing the value will cause both the AP ID and External Supplier Name to become unlinked';
    }
    if (isNotInherited) {
      return 'AP ID is currently not being inherited from corporate and is saved locally. To inherit delete both the AP ID and External Supplier Name and save';
    }
    return '';
  }, [isInherited, isNotInherited]);

  const formattedGlCodeOptions = useMemo(
    () =>
      glCodeOptions.map((glcd: any) => ({
        ...glcd,
        label: isInvoiceMPA
          ? glcd.label
          : generateInvoiceGlCodeLabel(
              glcd.label,
              glcd.remainingBudget,
              glcd.remainingInitialBudget,
              hasUsesInitialBudgetsPermission
            ),
      })),
    [glCodeOptions, hasUsesInitialBudgetsPermission, isInvoiceMPA]
  );

  const isInvoiceMPAEditable = useMemo(() => {
    const doesNotHasMpaPermission =
      !isPOMPA && !isSingleBudgetPeriod && !isMultiBudgetPeriods && isInvoiceMPA;
    const isSameUser = invoice?.clientId?.toString() === user?.ClientId?.toString();
    return doesNotHasMpaPermission && !isSameUser ? false : !isInvoiceEditDisabled;
  }, [
    invoice?.clientId,
    isInvoiceEditDisabled,
    isInvoiceMPA,
    isMultiBudgetPeriods,
    isPOMPA,
    isSingleBudgetPeriod,
    user?.ClientId,
  ]);

  const mpaPermissionNumber = useMemo(
    () =>
      getPermissionNumber(
        isInvoiceMPAEditable,
        isPOMPA,
        isSingleBudgetPeriod,
        isMultiBudgetPeriods,
        isInvoiceMPA
      ),
    [isInvoiceMPAEditable, isPOMPA, isSingleBudgetPeriod, isMultiBudgetPeriods, isInvoiceMPA]
  );

  const hasDistributionPermission = useMemo(
    () => hasPermission(DistributionPermissions, mpaPermissionNumber),
    [mpaPermissionNumber]
  );

  const { getGlCodesBudget } = useGLCodesBudgetApi(companyId);

  const initialDropdownIndex: number = useMemo(
    () =>
      glPeriods.findIndex((value) =>
        isWithinInterval(removeTime(new Date(poDetails?.creationDate)), {
          start: removeTime(new Date(value.startDate)),
          end: removeTime(new Date(value.endDate)),
        })
      ),
    [glPeriods, poDetails?.creationDate]
  );

  const optionsOnPoStatus = useMemo(() => {
    if (initialDropdownIndex === -1) {
      return glPeriods;
    } else if (poDetails.isOffline) {
      return getLastYearPeriod(glPeriods);
    } else {
      const poPeriod: GlPeriodObject = glPeriods[initialDropdownIndex];
      const futurePeriods = getAllFuturePeriods(glPeriods, initialDropdownIndex);
      return [poPeriod, ...futurePeriods];
    }
  }, [glPeriods, initialDropdownIndex, poDetails?.isOffline]);

  const formattedGLPeriods = useMemo(() => formatGLPeriodDropDown(glPeriods), [glPeriods]);

  const dropDownValues = useMemo(() => {
    const formattedData = formatGLPeriodDropDown(optionsOnPoStatus);
    let dropDownValues: GLPeriodDropdown[] = [];
    formattedData.forEach((item) => {
      formattedGLPeriods.forEach((value) => {
        if (item.fiscalYear === value.fiscalYear) {
          const newValue: GLPeriodDropdown = {
            ...value,
            items: value.items.filter((p) => item.items.find((v) => v.glPeriodId === p.glPeriodId)),
          };
          dropDownValues.push(newValue);
        }
      });
    });
    return dropDownValues;
  }, [formattedGLPeriods, optionsOnPoStatus]);

  const grandTotalAmt = Number(invoice.totalCostAmt) || 0;

  const {
    totalPercentage,
    totalAmount: budgetPeriodsTotalAmount,
    errorMessage,
    footerInfoMessage,
  } = useMemo(() => {
    let errorMessage: ErrorMessage = {
      totalAmount: [],
      totalPercentage: [],
    };
    let footerInfoMessage: string[] = [];
    let totalPercentage = 0;
    let totalAmount = 0;

    invoiceBudgetPeriods.forEach((item) => {
      totalPercentage = round(totalPercentage + Number(item.percentageAmount), 2);
      totalAmount = round(totalAmount + Number(item.amount), 2);
    });
    const roundedPercentage = round(totalPercentage);

    if (roundedPercentage !== 100) {
      errorMessage.totalPercentage = ['Total % Distribution must be equal to 100%'];
    }

    if (totalAmount !== grandTotalAmt) {
      errorMessage.totalAmount = ['Total $ Distribution must be equal to Total Amount'];
    }

    if (totalPercentage !== 100 && roundedPercentage === 100) {
      footerInfoMessage = [
        '100% of the $ value has been allocated. \n% Distribution values have been rounded.',
      ];
    }

    return { totalPercentage, totalAmount, errorMessage, footerInfoMessage };
  }, [invoiceBudgetPeriods, grandTotalAmt]);

  const {
    errors: budgetErrors,
    infos,
    saveErrors,
  } = useViewInvoiceBudgetTableErrors(
    distributionType,
    grandTotalAmt,
    glPeriods,
    isInvoiceEditDisabled,
    poDetails,
    throttledInvoiceBudgetPeriods,
    invoice
  );

  const informationList: string[] = useMemo(
    () =>
      [
        ...Object.values(infos)
          .map((i: any) => Object.values(i)?.flat())
          .flat()
          .map((item: any) => (typeof item === 'string' ? item : Object.values(item)?.flat()))
          .flat()
          .filter((item: any, i, arr) => arr.indexOf(item) === i),
      ] as string[],
    [infos]
  );

  const budgetErrorList: string[] = useMemo(
    () =>
      [
        ...Object.values(budgetErrors || {})
          .map((i: any) => Object.values(i)?.flat())
          .flat()
          .map((item: any) => (typeof item === 'string' ? item : Object.values(item)?.flat()))
          .flat()
          .filter((item: any, i, arr) => arr.indexOf(item) === i),
      ] as string[],
    [budgetErrors]
  );

  const saveErrorList: string[] = useMemo(
    () =>
      [
        ...Object.values(saveErrors || {})
          .map((i: any) => Object.values(i)?.flat())
          .flat()
          .map((item: any) => (typeof item === 'string' ? item : Object.values(item)?.flat()))
          .flat()
          .filter((item: any, i, arr) => arr.indexOf(item) === i),
      ] as string[],
    [saveErrors]
  );

  const errorMessageList = useMemo(
    () => [...errorMessage.totalAmount, ...errorMessage.totalPercentage] as string[],
    [errorMessage.totalAmount, errorMessage.totalPercentage]
  );

  const budgetPeriodsHasErrors = useMemo(
    () => !!budgetErrorList.length || !!errorMessageList.length,
    [budgetErrorList.length, errorMessageList.length]
  );

  const subChildCalculations = useCallback(
    (item: IInvoiceBudgetPeriod) => {
      const items = throttledInvoiceGLCodes.slice(0, -1).map((el) => {
        const allocation = invoice?.periodGlCdAllocations?.find(
          (allocation) =>
            allocation.glPeriodId === item.glPeriodId && allocation.glCdId === el.glCodeId
        );
        const budgetSummaryByPeriod = invoiceGlBudgetSummary?.find((s) => {
          const budget = s.glCdId === allocation?.glCdId && s.glPeriodId === allocation?.glPeriodId;
          return budget;
        }) as InvoiceGlBudgetSummaryResponse;

        const glLabel = generateGlCodeLabel(
          glCodesObject[el.glCode as string]?.label || '',
          budgetSummaryByPeriod,
          hasUsesInitialBudgetsPermission
        );
        return {
          glCode: glLabel,
          glSubTotal: hasGLChanged
            ? percentageCal(item.percentageAmount, el.glSubTotal)
            : allocation?.subTotal,
          taxableAmount: hasGLChanged
            ? percentageCal(item.percentageAmount, Number(el.taxableAmount))
            : allocation?.taxableAmount,
          discountAmt: hasGLChanged
            ? percentageCal(item.percentageAmount, el.discountAmt)
            : allocation?.discount,
          shipCostAmt: hasGLChanged
            ? percentageCal(item.percentageAmount, el.shipCostAmt)
            : allocation?.shipping,
          salesTaxAmt: hasGLChanged
            ? percentageCal(item.percentageAmount, el.salesTaxAmt)
            : allocation?.tax,
          glTotal: hasGLChanged
            ? percentageCal(item.percentageAmount, Number(el?.glTotal))
            : (allocation?.subTotal || 0) -
              (allocation?.discount || 0) +
              (allocation?.shipping || 0) +
              (allocation?.tax || 0),
          glPeriodId: item.glPeriodId,
          glCdId: el.glCodeId,
        };
      });

      const glTotalProps = getHigherValueIndex(items, item.amount, 'glTotal');
      if (glTotalProps?.difference && hasGLChanged && items[glTotalProps.higherValueIndex]) {
        items[glTotalProps.higherValueIndex].glTotal += glTotalProps.difference;
        items[glTotalProps.higherValueIndex].glSubTotal =
          (items[glTotalProps.higherValueIndex].glSubTotal || 0) + glTotalProps.difference;
      }
      return items;
    },
    [
      glCodesObject,
      hasGLChanged,
      hasUsesInitialBudgetsPermission,
      invoice?.periodGlCdAllocations,
      invoiceGlBudgetSummary,
      throttledInvoiceGLCodes,
    ]
  );

  // need separate thread
  const invoiceBudgetPeriodDataWithErrors = useMemo(
    () =>
      invoiceBudgetPeriods?.map((item, index) => ({
        ...item,
        expanded: expandedBudgetPeriodIds.includes(item.rowId),
        items: hasGLChanged ? [] : subChildCalculations(item),
        errors: budgetErrors?.[index] || {},
        informations: infos?.[index] || {},
        hasErrorsOrWarnings:
          Object.values(budgetErrors?.[index] || {})?.flat()?.length > 0 ||
          Object.values(infos?.[index] || {})?.flat()?.length > 0,
      })),
    [
      budgetErrors,
      expandedBudgetPeriodIds,
      hasGLChanged,
      infos,
      invoiceBudgetPeriods,
      subChildCalculations,
    ]
  );

  const confirmBeforeProceeding = useCallback(
    (acceptCallback: VoidFunction) => {
      if (isInvoiceBudgetPeriodDirty) {
        confirm({
          title: 'Unsaved Changes',
          content:
            'You have unsaved changes. Please go back (cancel) to first save them. Otherwise, you can proceed and your changes will be lost.',
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Discard and Proceed',
          width: '725px',
        }).then((isAccepted) => {
          if (isAccepted) {
            acceptCallback();
          }
        });
      } else {
        acceptCallback();
      }
    },
    [confirm, isInvoiceBudgetPeriodDirty]
  );
  const handleOpenSidePanel = useCallback(() => {
    if (!isInvoiceDirty) {
      openMpa();
    }
  }, [isInvoiceDirty, openMpa]);

  const handleCancelSidePanel = useCallback(() => {
    confirmBeforeProceeding(() => {
      setInvoiceBudgetPeriods(generateInitialAllocations(invoice));
      setDistributionType('Percentage');
      closeMpa();
    });
  }, [confirmBeforeProceeding, closeMpa, setInvoiceBudgetPeriods, invoice]);

  const calculateGLDistributionData = useCallback(
    (_invoiceGlCodes: InvoiceGlBreakoutItem[]): InvoiceGlBreakoutItem[] => {
      const __invoiceGlCodes = _invoiceGlCodes;
      if (!isInvoiceMPA) {
        return __invoiceGlCodes;
      }
      const multiPeriodData = __invoiceGlCodes.slice(0, -1)?.map((el) => ({
        ...el,
        expanded: expandedGLCode?.includes(el.glCode),

        items: !hasGLChanged
          ? invoice?.periodAllocations?.map((item, index) => {
              const currentPeriodAllocation = glPeriodsObject[item.glPeriodId];
              const { glPeriodId, startDate, endDate, fiscalYear, period } =
                currentPeriodAllocation || {};

              const { percentageAmount } = invoice?.periodAllocations?.[index];
              let infosList = Object.values(infos?.[index] || {}).flat();
              let errorsList = Object.values(budgetErrors?.[index] || {}).flat();
              const budgetSummaryByPeriod = invoiceGlBudgetSummary?.find(
                (s) => s.glPeriodId === item.glPeriodId && s.glCdId === el.glCodeId
              ) as InvoiceGlBudgetSummaryResponse;
              const glLabel = generateGlCodeLabel(
                el.glLabel,
                budgetSummaryByPeriod,
                hasUsesInitialBudgetsPermission
              );
              const accPeriodLabel = generateGlCodeLabel(
                `FY${fiscalYear}-Period ${period} (${fToMMDDYY(startDate)} - ${fToMMDDYY(
                  endDate
                )})`,
                budgetSummaryByPeriod,
                hasUsesInitialBudgetsPermission
              );

              const glAllocation = invoice?.periodGlCdAllocations.find(
                (allocation) =>
                  allocation.glCdId === el.glCodeId && allocation.glPeriodId === glPeriodId
              );

              return {
                ...item,
                glLabel: glLabel,
                accountingPeriod: accPeriodLabel,
                notes: el.notes,
                percentageAmount,
                infos: infosList,
                errors: errorsList,
                glCodeId: el.glCodeId,
                glSubTotal: glAllocation?.subTotal || 0,
                taxableAmount: glAllocation?.taxableAmount || 0,
                discountAmt: glAllocation?.discount || 0,
                shipCostAmt: glAllocation?.shipping || 0,
                salesTaxAmt: glAllocation?.tax || 0,
                glTotal: glAllocation
                  ? glAllocation?.subTotal -
                    glAllocation?.discount +
                    glAllocation?.shipping +
                    glAllocation?.tax
                  : 0,
                glPeriodId,
                acctPeriod: `${fToMMDDYY(startDate)} - ${fToMMDDYY(endDate)}`,
              };
            })
          : [],
      }));

      const totalsGlCodeItem = calculateTotals(multiPeriodData);

      return __invoiceGlCodes.length ? [...multiPeriodData, totalsGlCodeItem] : [];
    },
    [
      budgetErrors,
      expandedGLCode,
      glPeriodsObject,
      hasGLChanged,
      hasUsesInitialBudgetsPermission,
      infos,
      invoice?.periodAllocations,
      invoice?.periodGlCdAllocations,
      invoiceGlBudgetSummary,
      isInvoiceMPA,
    ]
  );

  useEffect(() => {
    if (!isInitiallyLoading) {
      if (!initialCalculate.current) {
        initialCalculate.current = true;
        return;
      }
      initialCalculate.current = true;
      setInvoiceGlCodes((prev) => calculateGLDistributionData(prev));
    }
  }, [calculateGLDistributionData, isInitiallyLoading, isInvoiceMPA, setInvoiceGlCodes]);

  useEffect(() => {
    if (hasGLChanged || isInvoiceBudgetPeriodDirty) {
      setExpandedGLCode([]);
    }
  }, [hasGLChanged, isInvoiceBudgetPeriodDirty]);

  useEffect(() => {
    if (isInvoiceBudgetPeriodDirty) {
      setExpandedBudgetPeriodIds([]);
    }
  }, [isInvoiceBudgetPeriodDirty]);

  const saveInvoice = useCallback(
    (glPeriodIdStartDate: string) => {
      const GlDistributionData = calculateGLDistributionData(invoiceGlCodes);
      setInvoiceGlCodes(GlDistributionData);
      const payload = {
        ...invoice,
        accrualDate: isInvoiceMPA ? null : glPeriodIdStartDate || null,
        lineItems: invoiceLineItems.map((invoiceLineItem) => ({
          ...invoiceLineItem,
          priceAmt: invoiceLineItem.priceAmt || 0,
        })),
        invoiceGlBreakouts: throttledInvoiceGLCodes
          .map((invoiceGlCode) => ({
            ...invoiceGlCode,
            discountAmt: Number(invoiceGlCode.discountAmt) || 0,
            shipCostAmt: Number(invoiceGlCode.shipCostAmt) || 0,
            salesTaxAmt: Number(invoiceGlCode.salesTaxAmt) || 0,
          }))
          .slice(0, -1),
        isAutoDistGlBreakout: !!invoice?.isAutoDistGlBreakout,
        invoiceDate: invoice?.invoiceDate
          ? (moment(invoice?.invoiceDate).format('YYYY-MM-DDThh:mm:ss') as unknown as Date)
          : (null as unknown as Date),
        receivedDate: invoice?.receivedDate
          ? (moment(invoice?.receivedDate).format('YYYY-MM-DDThh:mm:ss') as unknown as Date)
          : (null as unknown as Date),
      };
      return updateInvoice(payload);
    },
    [
      calculateGLDistributionData,
      invoice,
      invoiceGlCodes,
      invoiceLineItems,
      isInvoiceMPA,
      setInvoiceGlCodes,
      throttledInvoiceGLCodes,
      updateInvoice,
    ]
  );

  const isInvoiceLineItemsSubTotalZero = useMemo(
    () => throttledInvoiceLineItems.every((item) => Number(item.subTotal) === 0),
    [throttledInvoiceLineItems]
  );

  const { isEditPeriodDisable, editPeriodErrorMessage } = useMemo(() => {
    const isGrandTotalNotSame =
      Number(invoice?.totalCostAmt) !== Number(invoiceGlCodes[invoiceGlCodes.length - 1]?.glTotal);
    const isEditPeriodDisable =
      isInvoiceDirty || isInvoiceLineItemsSubTotalZero || isGrandTotalNotSame;
    let editPeriodErrorMessage: string[] = [];
    if (isInvoiceDirty) {
      editPeriodErrorMessage = ['Please save the document.'];
    } else if (isInvoiceLineItemsSubTotalZero) {
      editPeriodErrorMessage = ['Line item totals must be greater or less than 0.'];
    } else if (isGrandTotalNotSame) {
      editPeriodErrorMessage = ['Invoice Total amount should be equal to Invoice GL total amount.'];
    }
    return {
      isEditPeriodDisable,
      editPeriodErrorMessage,
    };
  }, [invoice?.totalCostAmt, invoiceGlCodes, isInvoiceDirty, isInvoiceLineItemsSubTotalZero]);

  const value: InvoiceContextType = useMemo(
    () => ({
      type,
      invoice,
      setInvoice,
      poId,
      expandedGLCode,
      setExpandedGLCode,
      getInvoice,
      title,
      glCodeOptions,
      errorList,
      warningList,
      invoiceGlCodes,
      setInvoiceGlCodes,
      glCodesObject,
      handleInputChange,
      isInvoiceSupplierNameDirty,
      showScans,
      setShowScans,
      invoiceLineItems: lineItemsWithErrorWarningStatus,
      lineItemsWithErrorWarningStatus,
      setInvoiceLineItems,
      invoiceId,
      companyId,
      splitOption,
      setSplitOption,
      splitLineItem,
      setSplitLineItem,
      errors,
      warnings,
      informations,
      totalAmount,
      setApIdDirty,
      setSupplierNameDirty,
      hasErrors,
      isInvoiceAPIDDirty,
      hasWarnings,
      isInvoiceInProgress,
      isInvoiceEditDisabled,
      isInvoiceLoading,
      hasDuplicateInvoiceNumber,
      refreshPage,
      setLinkedReceiptQuantity,
      linkedReceiptQuantity,
      isInvoiceDirty,
      isCreditMemo,
      lineItemInfos,
      setIsGlCodesDirty,
      initialInvoice,
      initialInvoiceLineItems,
      isInvoiceComplete,
      isValidatingDuplicateInvoiceNumber,
      selectedLineItemIds,
      setSelectedLineItemIds,
      expandedLineItemIds,
      setExpandedLineItemIds,
      receiptLineItemIds,
      saveInvoice,
      isUpdatingInvoice,
      getMessages,
      isInvoiceActionButtonDisabled,
      isLineItemClick,
      setIsLineItemClick,
      glBreakoutHasBothNegativeAndPositiveValues,
      isOpenMpa,
      openMpa,
      closeMpa,
      poDetails,
      isPOMPA,
      invoiceBudgetPeriods,
      setInvoiceBudgetPeriods,
      isEditPeriodDisable,
      editPeriodErrorMessage,
      getLinkIcon,
      isInherited,
      ApIdandExternalSupplierNametooltipMsg,
      invoiceGlBudgetSummary,
      getInvoiceGlBudgetSummary,
      hasUsesInitialBudgetsPermission,
      formattedGlCodeOptions,
      glPeriods,
      isDropDownLoading,
      isInvoiceBudgetPeriodDirty,
      isInitiallyLoading,

      dropDownValues,
      distributionType,
      setDistributionType,
      getGlCodesBudget,
      budgetErrors,
      infos,
      invoiceBudgetPeriodDataWithErrors,
      totalPercentage,
      budgetPeriodsHasErrors,
      footerInfoMessage,
      errorMessage,
      informationList,
      saveErrorList,
      errorMessageList,
      confirmBeforeProceeding,
      singleGlPeriodStartDate,
      setSingleGlPeriodStartDate,
      hasDistributionPermission,
      mpaPermissionNumber,
      isInvoiceMPA,
      handleOpenSidePanel,
      handleCancelSidePanel,
      isInvoiceMPAEditable,
      expandedBudgetPeriodIds,
      setExpandedBudgetPeriodIds,
      budgetPeriodsTotalAmount,

      throttledInvoiceGLCodes,
      throttledInvoiceLineItems,
      throttledInvoiceBudgetPeriods,

      areBudgetsThrottling,
      areGLCodesThrottling,
      areLineItemsThrottling,
      areLineItemsDebouncing,
      setAreLineItemsDebouncing,
      calculateGLDistributionData,
      hasGLChanged,
      getMatchingOcrLineItem,
      ocrResponse,
      handleHoverOcrField,
      numLineItems,
      setNumLineItems,
    }),
    [
      type,
      invoice,
      setInvoice,
      poId,
      expandedGLCode,
      getInvoice,
      title,
      glCodeOptions,
      errorList,
      warningList,
      invoiceGlCodes,
      setInvoiceGlCodes,
      glCodesObject,
      handleInputChange,
      isInvoiceSupplierNameDirty,
      showScans,
      lineItemsWithErrorWarningStatus,
      setInvoiceLineItems,
      invoiceId,
      companyId,
      splitOption,
      splitLineItem,
      errors,
      warnings,
      informations,
      totalAmount,
      setApIdDirty,
      setSupplierNameDirty,
      hasErrors,
      isInvoiceAPIDDirty,
      hasWarnings,
      isInvoiceInProgress,
      isInvoiceEditDisabled,
      isInvoiceLoading,
      hasDuplicateInvoiceNumber,
      refreshPage,
      linkedReceiptQuantity,
      isInvoiceDirty,
      isCreditMemo,
      lineItemInfos,
      initialInvoice,
      initialInvoiceLineItems,
      isInvoiceComplete,
      isValidatingDuplicateInvoiceNumber,
      selectedLineItemIds,
      expandedLineItemIds,
      receiptLineItemIds,
      saveInvoice,
      isUpdatingInvoice,
      getMessages,
      isInvoiceActionButtonDisabled,
      isLineItemClick,
      setIsLineItemClick,
      glBreakoutHasBothNegativeAndPositiveValues,
      isOpenMpa,
      openMpa,
      closeMpa,
      poDetails,
      isPOMPA,
      invoiceBudgetPeriods,
      setInvoiceBudgetPeriods,
      isEditPeriodDisable,
      editPeriodErrorMessage,
      getLinkIcon,
      isInherited,
      ApIdandExternalSupplierNametooltipMsg,
      invoiceGlBudgetSummary,
      getInvoiceGlBudgetSummary,
      hasUsesInitialBudgetsPermission,
      formattedGlCodeOptions,
      glPeriods,
      isDropDownLoading,
      isInvoiceBudgetPeriodDirty,
      isInitiallyLoading,
      dropDownValues,
      distributionType,
      getGlCodesBudget,
      budgetErrors,
      infos,
      invoiceBudgetPeriodDataWithErrors,
      totalPercentage,
      budgetPeriodsHasErrors,
      footerInfoMessage,
      errorMessage,
      informationList,
      saveErrorList,
      errorMessageList,
      confirmBeforeProceeding,
      singleGlPeriodStartDate,
      hasDistributionPermission,
      mpaPermissionNumber,
      isInvoiceMPA,
      handleOpenSidePanel,
      handleCancelSidePanel,
      isInvoiceMPAEditable,
      expandedBudgetPeriodIds,
      budgetPeriodsTotalAmount,
      throttledInvoiceGLCodes,
      throttledInvoiceLineItems,
      throttledInvoiceBudgetPeriods,
      areBudgetsThrottling,
      areGLCodesThrottling,
      areLineItemsThrottling,
      areLineItemsDebouncing,
      calculateGLDistributionData,
      hasGLChanged,
      getMatchingOcrLineItem,
      ocrResponse,
      handleHoverOcrField,
      numLineItems,
      setNumLineItems,
    ]
  );
  return <InvoiceContext.Provider value={value}>{children}</InvoiceContext.Provider>;
};

export default InvoiceDetailsContextProvider;
